import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { AdvancedTemplatesPage, AdvancedTemplatesPagedData, IAdvancedTemplateCloneModel, IAdvancedTemplateModel, IAdvancedTemplatesForDropdown,
    IAdvancedTemplatesModel } from './advanced-templates.model';

@Injectable()
export class AdvancedTemplatesService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'advancedtemplates';

    /** GET */
    public getAdvancedTemplate(id: string): Observable<SuccessResponse<IAdvancedTemplateModel> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IAdvancedTemplateModel>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/' + id,
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getAdvancedTemplatesForDropdown(): Observable<SuccessResponse<IAdvancedTemplatesForDropdown> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IAdvancedTemplatesForDropdown>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/dropdowns',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** PUT */
    public updateAdvancedTemplate(data: IAdvancedTemplateModel): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .put<SuccessResponse<boolean>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/' + data?.id,
                this.convertToFormData(data)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

     /** POST */
     public addAdvancedTemplate(data: IAdvancedTemplateModel): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT,
                this.convertToFormData(data)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public cloneAdvancedTemplate(data: IAdvancedTemplateCloneModel): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/' + data?.id + '/clones',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getAdvancedTemplates(page: AdvancedTemplatesPage): Observable<SuccessResponse<AdvancedTemplatesPagedData> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<AdvancedTemplatesPagedData>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/filters',
                page
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** DELETE */
    public deleteAdvancedTemplate(id: string): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                AdvancedTemplatesService.URL +
                AdvancedTemplatesService.ENDPOINT + '/' + id,
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    private convertToFormData(data: IAdvancedTemplateModel): FormData {
        const formData: FormData = new FormData();

        // Remove empty files
        const logos = [];
        const banners = [];
        data.headerSetting.contents?.forEach(content => {
            if (!content.isRemoved && (content.file || content.id)) {
                logos.push(content);
            }
        });
        data.bannerSetting.contents?.forEach(content => {
            if (!content.isRemoved && (content.file || content.id)) {
                banners.push(content);
            }
        });
        data.headerSetting.contents = [...logos];
        data.bannerSetting.contents = [...banners];

        // Remove empty translations
        const headerTranslations = [];
        const titleTranslations = [];
        const blockTranslations = [];
        data.headerSetting.translations?.forEach(translation => {
            if (translation.value && translation.value?.trim() !== '') {
                headerTranslations.push(translation);
            }
        });
        data.contentSetting.titleTranslations?.forEach(translation => {
            if (translation.value && translation.value?.trim() !== '') {
                titleTranslations.push(translation);
            }
        });
        data.contentSetting.blockTranslations?.forEach(translation => {
            if (translation.value && translation.value?.trim() !== '') {
                blockTranslations.push(translation);
            }
        });
        data.headerSetting.translations = [...headerTranslations];
        data.contentSetting.titleTranslations = [...titleTranslations];
        data.contentSetting.blockTranslations = [...blockTranslations];

        // Top level
        formData.append('name', data.name);
        formData.append('description', data.description ?? '');
        data.tags.forEach((tag, index) => {
            formData.append(`tags[${index}].id`, tag.id);
            formData.append(`tags[${index}].customIdentifier`, tag.customIdentifier ?? '');
            tag.translations?.forEach((translation, index2) => {
                formData.append(`tags[${index}].translations[${index2}].languageId`, translation.languageId);
                formData.append(`tags[${index}].translations[${index2}].value`, translation.value);
            });
        });
        formData.append('backgroundColor', data.backgroundColor);
        formData.append('textColor', data.textColor);
        formData.append('borderColor', data.borderColor);
        formData.append('contentColor', data.contentColor);
        formData.append('isDefault', JSON.stringify(data.isDefault));
        formData.append('customDomainId', data.customDomainId ?? '');

        // Header
        formData.append('headerSetting.showContent', JSON.stringify(data.headerSetting.showContent));
        formData.append('headerSetting.showText', JSON.stringify(data.headerSetting.showText));
        formData.append('headerSetting.headerContentSetting.backgroundColor', data.headerSetting.headerContentSetting.backgroundColor);
        formData.append('headerSetting.headerContentSetting.height', JSON.stringify(data.headerSetting.headerContentSetting.height));
        formData.append('headerSetting.headerContentSetting.isFullWidth', JSON.stringify(data.headerSetting.headerContentSetting.isFullWidth));
        formData.append('headerSetting.headerContentSetting.isPositionSticky', JSON.stringify(data.headerSetting.headerContentSetting.isPositionSticky));
        formData.append('headerSetting.headerContentSetting.isTopStackOrder', JSON.stringify(data.headerSetting.headerContentSetting.isTopStackOrder));
        formData.append('headerSetting.headerContentSetting.alignmentType', JSON.stringify(data.headerSetting.headerContentSetting.alignmentType));
        formData.append('headerSetting.headerContentSetting.padding.top', JSON.stringify(data.headerSetting.headerContentSetting.padding.top));
        formData.append('headerSetting.headerContentSetting.padding.bottom', JSON.stringify(data.headerSetting.headerContentSetting.padding.bottom));
        formData.append('headerSetting.headerContentSetting.padding.left', JSON.stringify(data.headerSetting.headerContentSetting.padding.left));
        formData.append('headerSetting.headerContentSetting.padding.right', JSON.stringify(data.headerSetting.headerContentSetting.padding.right));
        formData.append('headerSetting.headerTextSetting.backgroundColor', data.headerSetting.headerTextSetting.backgroundColor);
        formData.append('headerSetting.headerTextSetting.isPositionSticky', JSON.stringify(data.headerSetting.headerTextSetting.isPositionSticky));
        formData.append('headerSetting.headerTextSetting.isTopStackOrder', JSON.stringify(data.headerSetting.headerTextSetting.isTopStackOrder));
        formData.append('headerSetting.headerTextSetting.padding.top', JSON.stringify(data.headerSetting.headerTextSetting.padding.top));
        formData.append('headerSetting.headerTextSetting.padding.bottom', JSON.stringify(data.headerSetting.headerTextSetting.padding.bottom));
        formData.append('headerSetting.headerTextSetting.padding.left', JSON.stringify(data.headerSetting.headerTextSetting.padding.left));
        formData.append('headerSetting.headerTextSetting.padding.right', JSON.stringify(data.headerSetting.headerTextSetting.padding.right));
        data.headerSetting.contents.forEach((fileData, index) => {
            formData.append(`headerSetting.contents[${index}].id`, fileData.id ?? '');
            formData.append(`headerSetting.contents[${index}].languageId`, fileData.languageId);
            formData.append(`headerSetting.contents[${index}].contentMediaType`, JSON.stringify(fileData.contentMediaType));
            formData.append(`headerSetting.contents[${index}].file`, fileData.file);
        });
        data.headerSetting.translations?.forEach((translation, index) => {
            formData.append(`headerSetting.translations[${index}].languageId`, translation.languageId);
            formData.append(`headerSetting.translations[${index}].value`, translation.value);
        });

        // Banner
        formData.append('bannerSetting.backgroundColor', data.bannerSetting.backgroundColor);
        data.bannerSetting.contents.forEach((fileData, index) => {
            formData.append(`bannerSetting.contents[${index}].id`, fileData.id ?? '');
            formData.append(`bannerSetting.contents[${index}].languageId`, fileData.languageId);
            formData.append(`bannerSetting.contents[${index}].contentMediaType`, JSON.stringify(fileData.contentMediaType));
            formData.append(`bannerSetting.contents[${index}].contentContextType`, JSON.stringify(fileData.contentContextType));
            formData.append(`bannerSetting.contents[${index}].file`, fileData.file);
        });

        // Content
        formData.append('contentSetting.width', JSON.stringify(data.contentSetting.width));
        formData.append('contentSetting.marginX', JSON.stringify(data.contentSetting.marginX));
        formData.append('contentSetting.marginTop', JSON.stringify(data.contentSetting.marginTop));
        formData.append('contentSetting.marginBottom', JSON.stringify(data.contentSetting.marginBottom));
        formData.append('contentSetting.isBelowBanner', JSON.stringify(data.contentSetting.isBelowBanner));
        formData.append('contentSetting.borderColor', data.contentSetting.borderColor);
        formData.append('contentSetting.borderWidth', JSON.stringify(data.contentSetting.borderWidth));
        formData.append('contentSetting.borderRadius', JSON.stringify(data.contentSetting.borderRadius));
        formData.append('contentSetting.alignmentType', JSON.stringify(data.contentSetting.alignmentType));
        formData.append('contentSetting.padding.top', JSON.stringify(data.contentSetting.padding.top));
        formData.append('contentSetting.padding.bottom', JSON.stringify(data.contentSetting.padding.bottom));
        formData.append('contentSetting.padding.left', JSON.stringify(data.contentSetting.padding.left));
        formData.append('contentSetting.padding.right', JSON.stringify(data.contentSetting.padding.right));
        formData.append('contentSetting.contentTitleSetting.textColor', data.contentSetting.contentTitleSetting.textColor);
        formData.append('contentSetting.contentTitleSetting.fontFamily', data.contentSetting.contentTitleSetting.fontFamily);
        formData.append('contentSetting.contentTitleSetting.customFontId',data.contentSetting.contentTitleSetting.customFontId ?? '');
        formData.append('contentSetting.contentTitleSetting.backgroundColor', data.contentSetting.contentTitleSetting.backgroundColor);
        formData.append('contentSetting.contentTitleSetting.showContentTitle', JSON.stringify(data.contentSetting.contentTitleSetting.showContentTitle));
        formData.append('contentSetting.contentTitleSetting.isAlwaysVisible', JSON.stringify(data.contentSetting.contentTitleSetting.isAlwaysVisible));
        formData.append('contentSetting.contentBlockSetting.textColor', data.contentSetting.contentBlockSetting.textColor);
        formData.append('contentSetting.contentBlockSetting.fontFamily', data.contentSetting.contentBlockSetting.fontFamily);
        formData.append('contentSetting.contentBlockSetting.customFontId', data.contentSetting.contentBlockSetting.customFontId ?? '');
        formData.append('contentSetting.contentBlockSetting.backgroundColor', data.contentSetting.contentBlockSetting.backgroundColor);
        formData.append('contentSetting.contentMenuSetting.menuPositionType', JSON.stringify(data.contentSetting.contentMenuSetting.menuPositionType));
        formData.append('contentSetting.contentMenuSetting.alignmentType', JSON.stringify(data.contentSetting.contentMenuSetting.alignmentType));
        data.contentSetting.titleTranslations.forEach((translation, index) => {
            formData.append(`contentSetting.titleTranslations[${index}].languageId`, translation.languageId);
            formData.append(`contentSetting.titleTranslations[${index}].value`, translation.value);
        });
        data.contentSetting.blockTranslations.forEach((translation, index) => {
            formData.append(`contentSetting.blockTranslations[${index}].languageId`, translation.languageId);
            formData.append(`contentSetting.blockTranslations[${index}].value`, translation.value);
        });

        // Button
        formData.append('buttonSetting.width', JSON.stringify(data.buttonSetting.width));
        formData.append('buttonSetting.borderRadius', JSON.stringify(data.buttonSetting.borderRadius));
        formData.append('buttonSetting.isFullWidth', JSON.stringify(data.buttonSetting.isFullWidth));
        formData.append('buttonSetting.fontFamily', data.buttonSetting.fontFamily);
        formData.append('buttonSetting.alignmentType', JSON.stringify(data.buttonSetting.alignmentType));
        formData.append('buttonSetting.customFontId', data.buttonSetting.customFontId ?? '');
        formData.append('buttonSetting.buttonFullSetting.buttonColor.regularBorderColor', data.buttonSetting.buttonFullSetting.buttonColor.regularBorderColor);
        formData.append('buttonSetting.buttonFullSetting.buttonColor.regularBackgroundColor', data.buttonSetting.buttonFullSetting.buttonColor.regularBackgroundColor);
        formData.append('buttonSetting.buttonFullSetting.buttonColor.regularTextColor', data.buttonSetting.buttonFullSetting.buttonColor.regularTextColor);
        formData.append('buttonSetting.buttonFullSetting.buttonColor.hoverBorderColor', data.buttonSetting.buttonFullSetting.buttonColor.hoverBorderColor);
        formData.append('buttonSetting.buttonFullSetting.buttonColor.hoverBackgroundColor', data.buttonSetting.buttonFullSetting.buttonColor.hoverBackgroundColor);
        formData.append('buttonSetting.buttonFullSetting.buttonColor.hoverTextColor', data.buttonSetting.buttonFullSetting.buttonColor.hoverTextColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.regularBorderColor', data.buttonSetting.buttonHollowSetting.buttonColor.regularBorderColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.regularBackgroundColor', data.buttonSetting.buttonHollowSetting.buttonColor.regularBackgroundColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.regularTextColor', data.buttonSetting.buttonHollowSetting.buttonColor.regularTextColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.hoverBorderColor', data.buttonSetting.buttonHollowSetting.buttonColor.hoverBorderColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.hoverBackgroundColor', data.buttonSetting.buttonHollowSetting.buttonColor.hoverBackgroundColor);
        formData.append('buttonSetting.buttonHollowSetting.buttonColor.hoverTextColor', data.buttonSetting.buttonHollowSetting.buttonColor.hoverTextColor);
        data.buttonSetting.translations.forEach((translation, index) => {
            formData.append(`buttonSetting.translations[${index}].languageId`, translation.languageId);
            formData.append(`buttonSetting.translations[${index}].value`, translation.value);
            formData.append(`buttonSetting.translations[${index}].buttonType`, JSON.stringify(translation.buttonType));
        });

        // Footers
        data.footers.forEach((footer, index) => {
            formData.append(`footers[${index}].index`, JSON.stringify(footer.index));
            formData.append(`footers[${index}].footerId`, footer.footerId);
            formData.append(`footers[${index}].contentContextType`, JSON.stringify(footer.contentContextType));
        });

        return formData;
    }

}
